import React, { useState, useEffect } from 'react';
import { SpeakerHigh, SpeakerSlash, SkipForward, MusicNote } from '@phosphor-icons/react';
import {
  setMuted, isMuted, startAmbientMusic, stopAmbientMusic,
  nextTrack, getCurrentTrack, onTrackChange,
} from '../utils/audio';

const GENRE_COLOR = {
  funk:       '#E5BC44',
  house:      '#4DA3FF',
  pop:        '#FF3B7C',
  latin:      '#FF6B00',
  electronic: '#32D74B',
  trap:       '#AF52DE',
  rap:        '#FF6B00',
  soul:       '#D4A933',
};

export const AudioToggle = () => {
  const [muted, setMutedState] = useState(isMuted());
  const [track, setTrack] = useState(getCurrentTrack());

  useEffect(() => {
    if (!muted) try { startAmbientMusic(); } catch {}
    const unsub = onTrackChange((t) => setTrack(t));
    return () => unsub();
  }, [muted]);

  const toggle = () => {
    const next = !muted;
    setMuted(next);
    setMutedState(next);
    if (next) { try { stopAmbientMusic(); } catch {} ; setTrack(null); }
    else { try { startAmbientMusic(); } catch {} }
  };

  const skip = (e) => {
    e.stopPropagation();
    try { nextTrack(); } catch {}
  };

  return (
    <div className="flex items-center gap-1" data-testid="audio-controls">
      {/* Track pill — shows artist + title and a genre color accent */}
      {!muted && track && (
        <div
          className="hidden sm:flex items-center gap-1.5 px-2.5 py-1 rounded-sm border bg-[#0A0A0C]/70 max-w-[220px]"
          style={{ borderColor: `${GENRE_COLOR[track.genre] || '#FFD700'}66` }}
          data-testid="track-pill"
          title={`${track.artist} — ${track.title}`}
        >
          <MusicNote size={11} weight="fill" style={{ color: GENRE_COLOR[track.genre] || '#FFD700' }} />
          <div className="flex flex-col leading-tight min-w-0">
            <span className="font-mono text-[9px] uppercase tracking-[0.14em] truncate"
              style={{ color: GENRE_COLOR[track.genre] || '#FFD700' }}>{track.artist}</span>
            <span className="font-mono text-[10px] font-semibold text-white/90 truncate">{track.title}</span>
          </div>
        </div>
      )}
      {!muted && (
        <button
          onClick={skip}
          aria-label="Skip track"
          title="Next track"
          data-testid="audio-skip-btn"
          className="w-8 h-8 flex items-center justify-center rounded-sm bg-[#0A0A0C]/60 border border-[#2C2C35] hover:border-[#FFD700] text-[#A0A0A5] hover:text-[#FFD700] transition-all"
        >
          <SkipForward size={14} weight="fill" />
        </button>
      )}
      <button
        onClick={toggle}
        aria-label={muted ? 'Unmute' : 'Mute'}
        title={muted ? 'Unmute music & SFX' : 'Mute music & SFX'}
          data-testid="audio-toggle"
        className="w-9 h-9 flex items-center justify-center rounded-sm bg-[#0A0A0C]/60 border border-[#2C2C35] hover:border-[#FFD700] hover:text-[#FFD700] text-[#A0A0A5] transition-all"
      >
        {muted ? <SpeakerSlash size={16} weight="fill" /> : <SpeakerHigh size={16} weight="fill" />}
      </button>
    </div>
  );
};
